/**
 * Typed API client.
 *
 * Every request and response shape comes from `schema.d.ts`, which is generated
 * from the backend's own OpenAPI document. Never hand-edit that file — run
 * `npm run gen:api` instead. A renamed backend field then fails `npm run
 * typecheck` rather than surfacing as `undefined` at runtime.
 */
import createClient, { type Middleware } from 'openapi-fetch';

import type { paths } from './schema';

/** Access token lives in memory only — never localStorage (readable by any XSS). */
let accessToken: string | null = null;

/** Called when the session is gone for good, so the app can route to /login. */
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onUnauthorized(handler: () => void): void {
  onSessionExpired = handler;
}

/**
 * Refresh is a single flight: if ten requests 401 at once, one refresh runs and
 * the rest await the same promise instead of stampeding the endpoint.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      // The refresh token travels as an httpOnly cookie, so there is nothing to
      // send in the body and nothing readable from JS.
      const resp = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) return false;
      const data = (await resp.json()) as { access_token?: string };
      if (!data.access_token) return false;
      accessToken = data.access_token;
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export const API_BASE_URL: string = import.meta.env['VITE_API_URL'] ?? '';

const authMiddleware: Middleware = {
  onRequest({ request }) {
    if (accessToken) {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return request;
  },

  async onResponse({ request, response }) {
    if (response.status !== 401) return response;

    // Don't try to refresh a failed refresh or a failed login.
    if (request.url.includes('/v1/auth/refresh') || request.url.includes('/v1/auth/login')) {
      return response;
    }

    const refreshed = await refreshSession();
    if (!refreshed) {
      accessToken = null;
      onSessionExpired?.();
      return response;
    }

    // Replay the original request once with the new token.
    const retry = request.clone();
    retry.headers.set('Authorization', `Bearer ${accessToken}`);
    return fetch(retry);
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: 'include',
});

api.use(authMiddleware);

/** Narrow an openapi-fetch error into a message the UI can show. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === 'string') return detail;
    // FastAPI validation errors arrive as a list of {loc, msg, type}.
    if (Array.isArray(detail)) {
      const first = detail[0] as { msg?: string } | undefined;
      if (first?.msg) return first.msg;
    }
  }
  return fallback;
}
